// DynamicPage.jsx
import React, { useState, useEffect, Fragment } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Helmet } from 'react-helmet-async';
import {
  Calendar, TrendingUp, Filter, Star, DollarSign,
  Info, HelpCircle, BarChart3, BookOpen
} from 'lucide-react';
import { supabase } from '../lib/supabaseClient';
import { ProductList } from './ProductList';
import { ErrorBoundary } from './ErrorBoundary';
import { PageContentSection } from './PageContentSection';
import { PageStats } from './PageStats';
import { RelatedPages } from './ui/RelatedPages';
import { format } from 'date-fns';
import { initFAQToggles } from '../utils/faqToggle.js';
import { evaluateGuide, guideMeta, canonicalUrl, humanizeSlug, stripEmbeddedBreadcrumbs, ROBOTS_NOINDEX } from '../utils/seoPolicy.js';
import { useClaimRobots } from '../utils/robotsOwnership.js';
import approvedGuidePaths from '../generated/seo-approved-paths.json';
import { EditorialGuideRoute } from './EditorialGuideRoute';
import { getEditorialGuide } from '../content/editorial';

/* --------------------------- Fetch Page Data --------------------------- */
async function fetchPageData(slug) {
  const { data, error } = await supabase
    .from('seo_auto_pages')
    .select('*')
    .eq('slug', slug)
    .eq('status', 'published')
    .maybeSingle();

  if (error) throw new Error(error.message);
  return data;
}

/* ------------------------- Page Type Config ---------------------------- */
const getPageTypeConfig = (type) => {
  switch (type) {
    case 'features':
    case 'feature':
      return { icon: Star, color: 'from-blue-600 to-indigo-600', bgClass: 'bg-blue-50' };
    case 'deals':
    case 'deal':
      return { icon: TrendingUp, color: 'from-green-600 to-emerald-600', bgClass: 'bg-green-50' };
    case 'brands':
    case 'brand':
      return { icon: Filter, color: 'from-purple-600 to-violet-600', bgClass: 'bg-purple-50' };
    case 'use-cases':
    case 'use-case':
      return { icon: Calendar, color: 'from-orange-600 to-red-600', bgClass: 'bg-orange-50' };
    case 'budgets':
    case 'budget':
      return { icon: DollarSign, color: 'from-green-600 to-teal-600', bgClass: 'bg-teal-50' };
    case 'trending':
      return { icon: TrendingUp, color: 'from-pink-600 to-rose-600', bgClass: 'bg-pink-50' };
    default:
      return { icon: Star, color: 'from-gray-600 to-slate-600', bgClass: 'bg-gray-50' };
  }
};

/* ------------------------- Budget Ranges By Category ---------------------------- */
/* Adjust or extend these as you like, or fetch from DB later */
const priceRangesByCategory = {
  /* 🖥️ Monitors */
  'best-computer-monitors': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $150', value: '0-150' },
    { label: 'Under $200', value: '0-200' },
    { label: 'Under $300', value: '0-300' },
    { label: '$150 – $300', value: '150-300' },
    { label: '$300 – $600', value: '300-600' },
    { label: 'Above $600 ($600+)', value: '600-10000' }
  ],

  /* 🎮 Graphics Cards (GPUs) */
  'best-graphics-cards-gpu': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $200', value: '0-200' },
    { label: '$200 – $400', value: '200-400' },
    { label: '$400 – $800', value: '400-800' },
    { label: '$800 – $1500+', value: '800-15000' }
  ],

  /* ⚙️ CPU / Processors */
  'best-processors-cpu': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $150', value: '0-150' },
    { label: '$150 – $300', value: '150-300' },
    { label: '$300 – $600', value: '300-600' },
    { label: '$600 – $1000+', value: '600-10000' }
  ],

  /* 🧠 Memory / RAM */
  'fastest-gaming-ram-memory': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $50', value: '0-50' },
    { label: '$50 – $100', value: '50-100' },
    { label: '$100 – $200+', value: '100-500' }
  ],

  /* 🎧 Gaming Headsets */
  'best-gaming-headsets': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $50', value: '0-50' },
    { label: '$50 – $100', value: '50-100' },
    { label: '$100 – $200+', value: '100-500' }
  ],

  /* 🖱️ Gaming Mice */
  'best-gaming-mice': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $30', value: '0-30' },
    { label: '$30 – $70', value: '30-70' },
    { label: '$70 – $150+', value: '70-150' }
  ],

  /* ⌨️ Mechanical Keyboards */
  'best-mechanical-keyboards': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $50', value: '0-50' },
    { label: '$50 – $120', value: '50-120' },
    { label: '$120 – $250+', value: '120-500' }
  ],

  /* 💻 Gaming Laptops */
  'best-laptops-for-gaming': [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $800', value: '0-800' },
    { label: '$800 – $1500', value: '800-1500' },
    { label: '$1500 – $2500+', value: '1500-5000' }
  ],

  /* 🧩 Default Fallback */
  default: [
    { label: 'All Prices', value: 'all' },
    { label: 'Under $100', value: '0-100' },
    { label: '$100 – $300', value: '100-300' },
    { label: '$300 – $1000+', value: '300-10000' }
  ]
};

/* --------------------------- Budget Dropdown Component --------------------------- */
const BudgetRangeDropdown = ({ categoryKey, onChange }) => {
  const options = priceRangesByCategory[categoryKey] || priceRangesByCategory.default;
  const [value, setValue] = useState('all');

  useEffect(() => {
    // reset when categoryKey changes
    setValue('all');
    onChange && onChange('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey]);

  const handle = (e) => {
    setValue(e.target.value);
    onChange && onChange(e.target.value);
  };

  return (
    <div className="flex items-center gap-3 mt-3 sm:mt-0">
      <label htmlFor="budget-range" className="text-sm text-gray-700 font-medium">Budget</label>
      <select
        id="budget-range"
        value={value}
        onChange={handle}
        className="text-sm border rounded-md p-2 min-w-[160px] focus:outline-none focus:ring-2 focus:ring-blue-300"
      >
        {options.map(opt => (
          <option key={opt.value} value={opt.value}>{opt.label}</option>
        ))}
      </select>
    </div>
  );
};

/* --------------------------- Safe renderer for "secondary_type_page" --------------------------- */
/**
 * Accepts: string | array | object (possibly nested)
 * Returns: React nodes (never raw object)
 */
function renderSecondaryType(value) {
  if (value == null) return null;

  // If it's a simple string, return as text (preserve line breaks)
  if (typeof value === 'string') {
    return <div dangerouslySetInnerHTML={{ __html: value }} />;
  }

  // If it's an array, render each item recursively
  if (Array.isArray(value)) {
    return (
      <div className="space-y-2">
        {value.map((item, idx) => (
          <div key={idx}>
            {renderSecondaryType(item)}
          </div>
        ))}
      </div>
    );
  }

  // If it's an object, render entries; if nested objects/arrays then render recursively
  if (typeof value === 'object') {
    return (
      <div className="space-y-2">
        {Object.entries(value).map(([k, v]) => (
          <div key={k} className="flex gap-2">
            <strong className="min-w-[110px]">{k.replace(/[-_]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong>
            <div className="flex-1">{renderSecondaryType(v)}</div>
          </div>
        ))}
      </div>
    );
  }

  // Fallback to string conversion
  return <div>{String(value)}</div>;
}

/* --------------------------- Product Count RPC --------------------------- */
/* Keep your existing RPC but ensure it accepts min/max nulls */
export async function fetchProductCount(filters) {
  try {
    const { data, error } = await supabase.rpc('search_products', {
      p_search_text: filters.search || null,
      p_category: filters.category && filters.category !== 'all' ? filters.category : null,
      p_brands: filters.brands?.length ? filters.brands : null,
      p_min_price: filters.minPrice != null ? Number(filters.minPrice) : null,
      p_max_price: filters.maxPrice != null ? Number(filters.maxPrice) : null,
      p_min_rating: filters.rating != null ? Number(filters.rating) : null,
      p_product_tags: filters.productTags?.length ? filters.productTags : null,
      p_sort: filters.sort || 'featured',
      p_limit: 1,
      p_offset: 0
    });

    if (error) {
      console.error('fetchProductCount RPC error:', error);
      return 0;
    }
    return data?.[0]?.total_count || 0;
  } catch (err) {
    console.error('fetchProductCount error:', err);
    return 0;
  }
}

/* --------------------------- Detect Budget Variant & Extract Price Range --------------------------- */
const detectBudgetVariant = (slug) => {
  if (!slug) return null;
  
  // Match patterns like "under-200", "200-400", "above-600"
  const underMatch = slug.match(/under[- ](\d+)/i);
  const rangeMatch = slug.match(/(\d+)[- ](\d+)/);
  const aboveMatch = slug.match(/above[- ](\d+)/i);
  
  if (underMatch) {
    return { min: 0, max: parseInt(underMatch[1]) };
  } else if (rangeMatch) {
    return { min: parseInt(rangeMatch[1]), max: parseInt(rangeMatch[2]) };
  } else if (aboveMatch) {
    return { min: parseInt(aboveMatch[1]), max: 99999 };
  }
  
  return null;
};

/* --------------------------- Generate Friendly Budget Links --------------------------- */
const FriendlyBudgetLinks = ({ pageData, categoryKey, onBudgetSelect }) => {
  const options = priceRangesByCategory[categoryKey] || priceRangesByCategory.default;
  const baseSlug = pageData.slug.replace(/-(under-\d+|\d+-\d+|above-\d+)$/i, '');
  
  const handleBudgetClick = (e, optValue) => {
    e.preventDefault();
    // Scroll to budget dropdown and trigger it
    const budgetDropdown = document.getElementById('budget-range');
    if (budgetDropdown) {
      budgetDropdown.value = optValue;
      budgetDropdown.dispatchEvent(new Event('change', { bubbles: true }));
      budgetDropdown.scrollIntoView({ behavior: 'smooth', block: 'center' });
      budgetDropdown.focus();
    }
  };
  
  const budgetLinks = options.slice(1).map(opt => {
    let label = opt.label;
    
    if (opt.value === 'all') return null;
    
    const [min, max] = opt.value.split('-').map(Number);
    
    if (min === 0 && max) {
      label = `Under $${max}`;
    } else if (max >= 9999) {
      label = `Above $${min}`;
    } else {
      label = `$${min} – $${max}`;
    }
    
    return (
      <li key={opt.value}>
        <a 
          href={`#budget-range`}
          onClick={(e) => handleBudgetClick(e, opt.value)}
          className="text-blue-600 hover:underline dark:text-blue-400 cursor-pointer"
        >
          {pageData.title} {label}
        </a>
      </li>
    );
  }).filter(Boolean);
  
  return (
    <div className="prose max-w-none bg-white dark:bg-slate-800 rounded-xl shadow-sm overflow-hidden">
      <div className="friendly-budget p-6">
        <h3 className="text-lg font-semibold mb-3 text-gray-900 dark:text-white">Shop by Budget:</h3>
        <ul className="space-y-2">
          {budgetLinks}
        </ul>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-4 mb-0">
          Click any budget range to filter products below.
        </p>
      </div>
    </div>
  );
};

/* --------------------------- DynamicPage Component ----------------------------- */
export function DynamicPage() {
  useClaimRobots();
  const { pageType, slug } = useParams();
  const fullSlug = slug ? `${slug}` : pageType;

  // A registered static editorial guide is self-contained: no database query, and its robots/canonical come from the
  // fixture + manifest approval (EditorialGuideRoute), never from the legacy seo_auto_pages row.
  const editorial = getEditorialGuide(fullSlug);

  const { data: pageData, isLoading, error } = useQuery({
    queryKey: ['dynamicPage', fullSlug],
    queryFn: () => fetchPageData(fullSlug),
    enabled: !editorial,
  });

  // parsed filters from pageData
  const parseFiltersFromPage = (pData) => {
    try {
      if (!pData) return {};
      const raw = pData.filters;
      if (!raw) return {};
      return typeof raw === 'string' ? JSON.parse(raw) : raw;
    } catch (err) {
      console.error('Failed to parse page filters', err);
      return {};
    }
  };

  const parsedFilters = parseFiltersFromPage(pageData);

  // Detect if this is a budget variant page
  const priceRange = detectBudgetVariant(fullSlug);
  const isBudgetVariant = priceRange !== null;
  
  // Extract base title (without budget suffix)
  const baseTitle = pageData?.title || '';
  
  // month/year banner if requested
  const monthYear = parsedFilters?.monthYear ? format(new Date(), 'MMMM yyyy') : null;

  // product filters state (so dropdown updates ProductList)
  // If budget variant, override price filters from slug
  const [productFilters, setProductFilters] = useState({
    search: parsedFilters.search?.trim() || null,
    category: parsedFilters.category || 'all',
    brands: Array.isArray(parsedFilters.brands) ? parsedFilters.brands : (parsedFilters.brands ? [parsedFilters.brands] : []),
    productTags: Array.isArray(parsedFilters.productTags) ? parsedFilters.productTags : (parsedFilters.productTags ? [parsedFilters.productTags] : []),
    minPrice: isBudgetVariant ? priceRange.min : (parsedFilters.price?.min ?? null),
    maxPrice: isBudgetVariant ? priceRange.max : (parsedFilters.price?.max ?? null),
    rating: parsedFilters.rating ?? null,
    stockStatus: parsedFilters.stockStatus || '',
    sort: parsedFilters.sortBy || 'featured',
    monthYear: parsedFilters.monthYear || null
  });

  // keep local budget state (string value like '200-500')
  const [budgetRange, setBudgetRange] = useState('all');

  // When pageData or parsed filters change, initialize state (so it works on navigation)
  useEffect(() => {
    const newParsed = parseFiltersFromPage(pageData);
    const newPriceRange = detectBudgetVariant(fullSlug);
    const isVariant = newPriceRange !== null;
    
    setProductFilters(prev => ({
      ...prev,
      search: newParsed.search?.trim() || null,
      category: newParsed.category || 'all',
      brands: Array.isArray(newParsed.brands) ? newParsed.brands : (newParsed.brands ? [newParsed.brands] : []),
      productTags: Array.isArray(newParsed.productTags) ? newParsed.productTags : (newParsed.productTags ? [newParsed.productTags] : []),
      minPrice: isVariant ? newPriceRange.min : (newParsed.price?.min ?? null),
      maxPrice: isVariant ? newPriceRange.max : (newParsed.price?.max ?? null),
      rating: newParsed.rating ?? null,
      stockStatus: newParsed.stockStatus || '',
      sort: newParsed.sortBy || 'featured',
      monthYear: newParsed.monthYear || null
    }));

    if (pageData?.content) {
      initFAQToggles();
    }

    setBudgetRange('all');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageData?.id, pageData?.filters, fullSlug]);

  // handle budget dropdown changes (functional update to avoid stale closures)
  const handleBudgetChange = (value) => {
    setBudgetRange(value);
    if (value === 'all') {
      setProductFilters(prev => ({ ...prev, minPrice: null, maxPrice: null }));
    } else {
      const [minStr, maxStr] = value.split('-');
      const min = minStr ? Number(minStr) : null;
      const max = maxStr ? Number(maxStr) : null;
      setProductFilters(prev => ({ ...prev, minPrice: min, maxPrice: max }));
    }
  };

  // product count (keeps in sync when productFilters change)
  const { data: productCount } = useQuery({
    queryKey: ['productCount', productFilters],
    queryFn: () => fetchProductCount(productFilters),
    enabled: !!pageData,
    staleTime: 1000 * 60 * 1 // 1 min
  });

  // A budget variant may only link to its base guide if that guide really exists (published).
  const budgetBaseSlug = fullSlug ? fullSlug.replace(/-(under-\d+|\d+-\d+|above-\d+)$/i, '') : null;
  const { data: budgetBasePage } = useQuery({
    queryKey: ['dynamicPage', budgetBaseSlug],
    queryFn: () => fetchPageData(budgetBaseSlug),
    enabled: isBudgetVariant && !!budgetBaseSlug && budgetBaseSlug !== fullSlug,
  });
  const baseGuideExists = !!budgetBasePage;

  if (editorial) return <EditorialGuideRoute guide={editorial} />;
  if (isLoading) return <div className="text-center py-12">Loading...</div>;
  if (error || !pageData) {
    // Missing page: never indexable and never a canonical target.
    return (
      <>
        <Helmet>
          <title>Page not found | TechieDay</title>
          <meta name="robots" content={ROBOTS_NOINDEX} />
          <meta name="googlebot" content={ROBOTS_NOINDEX} />
        </Helmet>
        <div className="text-center text-red-600">Page not found.</div>
      </>
    );
  }

  const { icon: Icon, color, bgClass } = getPageTypeConfig(pageData.primary_type_page || pageType);
  
  // Generate dynamic title and meta description
  const dynamicTitle = isBudgetVariant 
    ? `${baseTitle} ${priceRange.max >= 9999 ? `Above $${priceRange.min}` : priceRange.min === 0 ? `Under $${priceRange.max}` : `$${priceRange.min}-$${priceRange.max}`}`
    : baseTitle;
  
  const dynamicDescription = isBudgetVariant
    ? `Find the best ${baseTitle.toLowerCase()} ${priceRange.max >= 9999 ? `above $${priceRange.min}` : priceRange.min === 0 ? `under $${priceRange.max}` : `between $${priceRange.min} and $${priceRange.max}`}. Expert reviews, comparisons, and deals updated for ${new Date().getFullYear()}.`
    : `${pageData.description} — Compare top products across all budgets and features.`;

  /* Determine categoryKey for budget dropdown:
     pageData.categories could be an array or a slug string or null.
     We'll try to pick the first entry if array, otherwise fallback to 'default'. */
  const categoryKey = (() => {
    if (!pageData) return 'default';
    const c = pageData.categories;
    if (!c) return 'default';
    if (typeof c === 'string') return c;
    if (Array.isArray(c) && c.length) return c[0];
    // if object with slug or key use that
    if (typeof c === 'object' && c !== null) {
      // try common keys
      if (c.slug) return c.slug;
      if (c.key) return c.key;
      // else stringify first key
      const first = Object.keys(c)[0];
      return first || 'default';
    }
    return 'default';
  })();

  // Metadata authority for editorial guides: title/description from the editorial record,
  // canonical + robots from the shared SEO policy (explicit manifest approval required).
  const baseSlug = pageData.slug.replace(/-(under-\d+|\d+-\d+|above-\d+)$/i, '');
  const seo = evaluateGuide({ path: `/${pageData.slug}`, page: pageData, approved: approvedGuidePaths });
  const { title: metaTitle, description: metaDescription } = guideMeta(pageData);

  // Breadcrumb: only real, normalized destinations. The category crumb needs a real category slug;
  // the current page is the last item without a link.
  const hasCategoryCrumb = categoryKey !== 'default';
  const breadcrumbItems = [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": canonicalUrl("/") },
    ...(hasCategoryCrumb ? [{ "@type": "ListItem", "position": 2, "name": humanizeSlug(categoryKey), "item": canonicalUrl(`/category/${categoryKey}`) }] : []),
    { "@type": "ListItem", "position": hasCategoryCrumb ? 3 : 2, "name": dynamicTitle }
  ];

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        {metaDescription && <meta name="description" content={metaDescription} />}
        {pageData.meta_keywords && (
          <meta name="keywords" content={Array.isArray(pageData.meta_keywords) ? pageData.meta_keywords.join(', ') : pageData.meta_keywords} />
        )}
        <meta name="robots" content={seo.robots} />
        <meta name="googlebot" content={seo.robots} />
        {seo.canonical && <link rel="canonical" href={seo.canonical} />}
        {pageData.updated_at && (
          <>
            <meta property="article:modified_time" content={new Date(pageData.updated_at).toISOString()} />
            <meta name="last-modified" content={new Date(pageData.updated_at).toISOString()} />
          </>
        )}
        <meta property="og:title" content={metaTitle} />
        {metaDescription && <meta property="og:description" content={metaDescription} />}
        {seo.canonical && <meta property="og:url" content={seo.canonical} />}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TechieDay" />
        <meta name="twitter:card" content="summary_large_image" />
        
        {/* Breadcrumb Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": breadcrumbItems
          })}
        </script>
        
        {/* Price Range Schema for Budget Variants */}
        {isBudgetVariant && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": dynamicTitle,
              "description": dynamicDescription,
              "offers": {
                "@type": "AggregateOffer",
                "priceCurrency": "USD",
                "lowPrice": priceRange.min,
                "highPrice": priceRange.max >= 9999 ? null : priceRange.max
              }
            })}
          </script>
        )}
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-slate-900 dark:to-slate-800">
        <header className={`bg-gradient-to-r ${color} text-white`} role="banner">
          <div className="max-w-7xl mx-auto px-4 py-12 text-center">
            <Icon className="w-10 h-10 mx-auto mb-4" aria-hidden="true" />
            <h1 className="text-xl sm:text-3xl font-bold">
              {dynamicTitle}
              {monthYear && <span className="block sm:inline sm:ml-2 text-2xl sm:text-3xl mt-2 sm:mt-0">({monthYear})</span>}
            </h1>
            <p className="text-xl opacity-90 mt-2">
              {dynamicDescription}
              {monthYear && <span className="block sm:inline"> — Updated for {monthYear}</span>}
            </p>
          </div>
        </header>

        {/* Breadcrumb Navigation */}
        <nav aria-label="breadcrumb" className="max-w-7xl mx-auto px-4 pt-8 text-white">
          <ol className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <li><a href="/" className="hover:text-blue-600 dark:hover:text-blue-400">Home</a></li>
            {hasCategoryCrumb && (
              <>
                <li>›</li>
                <li><a href={`/category/${categoryKey}`} className="hover:text-blue-600 dark:hover:text-blue-400 capitalize">{categoryKey.replace(/-/g, ' ')}</a></li>
              </>
            )}
            {isBudgetVariant && (
              <>
                <li>›</li>
                <li>{baseGuideExists ? <a href={`/${baseSlug}`} className="hover:text-blue-600 dark:hover:text-blue-400">{baseTitle}</a> : <span>{baseTitle}</span>}</li>
                <li>›</li>
                <li className="font-medium text-gray-900 dark:text-white">
                  {priceRange.max >= 9999 ? `Above $${priceRange.min}` : priceRange.min === 0 ? `Under $${priceRange.max}` : `$${priceRange.min}-$${priceRange.max}`}
                </li>
              </>
            )}
            {!isBudgetVariant && (
              <>
                <li>›</li>
                <li className="font-medium text-gray-900 dark:text-white">{baseTitle}</li>
              </>
            )}
          </ol>
        </nav>

        <div className="max-w-7xl mx-auto px-4 py-8">
          {/* Unique Intro Section */}
          <div className="mb-6 prose max-w-none dark:prose-invert bg-white dark:bg-slate-800 rounded-xl shadow-sm p-6">
            {isBudgetVariant ? (
              <p className="text-gray-700 dark:text-gray-300">
                This guide focuses on <strong>{baseTitle.toLowerCase()} {priceRange.max >= 9999 ? `above $${priceRange.min}` : priceRange.min === 0 ? `under $${priceRange.max}` : `between $${priceRange.min} and $${priceRange.max}`}</strong>. 
                Perfect for buyers seeking quality products within this budget range. 
                If you'd like to explore other price ranges, check out our{' '}
                {baseGuideExists
                  ? <a href={`/${baseSlug}`} className="text-blue-600 hover:underline dark:text-blue-400">complete {baseTitle.toLowerCase()} guide</a>
                  : <span>complete {baseTitle.toLowerCase()} guide</span>} for all budgets.
              </p>
            ) : (
              <p className="text-gray-700 dark:text-gray-300">
                Discover premium {baseTitle.toLowerCase()} across all price tiers. 
                From budget-friendly picks to top-tier models — this comprehensive guide helps you find the perfect balance of performance and value. 
                Use the budget filters below to narrow down options for your price range.
              </p>
            )}
          </div>
          
          <PageStats pageData={pageData} bgClass={bgClass} productCount={productCount || 0} pageType={pageData.page_type} />

          <main className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-6">
            <div className="lg:col-span-2 space-y-8">
              {pageData.content && (
                <PageContentSection title="Detail" icon={BookOpen} bgClass={bgClass}>
                  <div className="prose max-w-none dark:prose-invert" dangerouslySetInnerHTML={{ __html: stripEmbeddedBreadcrumbs(pageData.content) }} />
                </PageContentSection>
              )}

              {/* Friendly Budget Links - Only show on non-budget variant pages */}
              {!isBudgetVariant && <FriendlyBudgetLinks pageData={pageData} categoryKey={categoryKey} onBudgetSelect={handleBudgetChange} />}

              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="bg-green-50 p-4 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <BarChart3 className="w-5 h-5 text-gray-600 mr-2" />
                    <h3 className="flex items-center text-xl font-semibold text-gray-900">
                      {pageData.page_type === 'deal' || pageData.page_type === 'deals' ? 'Featured Deals' : 'Featured Products'}
                      {productCount > 0 && (
                        <span className="ml-2 text-sm font-normal text-gray-500">({productCount} {productCount === 1 ? 'item' : 'items'})</span>
                      )}
                    </h3>
                  </div>

                  {/* Budget dropdown tied to category */}
                  <BudgetRangeDropdown categoryKey={categoryKey} onChange={handleBudgetChange} />
                </div>

                <div className="p-6">
                  <ErrorBoundary>
                    <ProductList filters={productFilters} view="grid" suppressSeo />
                  </ErrorBoundary>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <PageContentSection
                title="Types"
                icon={Info}
                bgClass={bgClass}
              >
                {/* Use safe renderer here rather than passing raw object into content prop */}
                {renderSecondaryType(pageData.secondary_type_page)}
              </PageContentSection>

              {pageData.target_keywords && (
                <PageContentSection title="Searches" icon={Filter} bgClass={bgClass}>
                  <div className="flex flex-wrap gap-2">
                    {Array.isArray(pageData.target_keywords) ? pageData.target_keywords.join(', ') : pageData.target_keywords}
                  </div>
                </PageContentSection>
              )}

              <PageContentSection title="Related Pages" icon={HelpCircle} bgClass={bgClass}>
                <RelatedPages
                  currentSlug={pageData.slug}
                  categories={pageData.categories}
                  primaryType={pageData.primary_type_page}
                />
              </PageContentSection>

              <PageContentSection title="Quick Guide" icon={HelpCircle} content="Tips and info coming soon." bgClass={bgClass} />
            </div>
          </main>
        </div>
      </div>
    </>
  );
}
